import { ReviewAttachments } from "./ReviewAttachments";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBriefDocument, useApproveBrief, useNudgeBrief, useRejectBrief, useSubmitPublishedUrl } from "../../hooks/useCampaigns";
import { useEditOutput } from "../../hooks/useOutputDetail";
import { CopyButton } from "../ui/CopyButton";
import { OutputView } from "../output/OutputView";
import { GroundingIndicator } from "../output/GroundingIndicator";
import { BriefProvenanceAlerts } from "./BriefProvenanceAlerts";
import type { Brief, OutputDocument } from "../../../../shared/types";
import { buildBrandScopedPath } from "../../lib/brand-navigation";
import { claimRiskMessage, hasClaimRiskBlockedTrust, reviewTrustBadgeCopy } from "../../lib/review-trust-state";
import { Badge } from "../ui/Badge";
import {
  getBlockingReviewAlertDetails,
  getBlockingReviewAlerts,
  hasBlockingSourceRisk,
} from "../../../../shared/review-source-risk";

interface Props {
  brief: Brief;
  brandId: string;
  onPublished?: (brief: Brief) => void;
  /** Called once the approval is recorded (the brief then leaves the Review queue). */
  onApproved?: (brief: Brief) => void;
}

interface YoutubeScript {
  title?: string;
  description?: string;
  tags?: string[];
  scenes?: { text: string; duration_s: number }[];
  outro?: string;
}

function parseScript(content: string): YoutubeScript | null {
  try {
    return JSON.parse(content) as YoutubeScript;
  } catch {
    return null;
  }
}

const PASTE_BACK_WINDOW_MS = 30 * 60 * 1000;

function safeRecordedUrl(rawValue: string | null | undefined): string | null {
  if (!rawValue?.trim()) return null;
  try {
    const parsed = new URL(rawValue);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

function buildPerformanceFollowupPrompt(brief: Brief, publishedUrl: string): string {
  const surfaceLabel = brief.surface ?? brief.platform ?? "content";
  return [
    `Review the published ${surfaceLabel} item "${brief.title}".`,
    `Published URL: ${publishedUrl}`,
    "Capture the first performance signals or operator observations you have.",
    "Then update durable brand learnings, packet context, or future brief guidance if this result should change how we write or publish.",
  ].join("\n");
}

export function ApproveAndPostYouTube({ brief, brandId, onPublished, onApproved }: Props) {
  const navigate = useNavigate();
  const [publishedUrl, setPublishedUrl] = useState(brief.published_url ?? "");
  const [approvedAt, setApprovedAt] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [sourceRiskAcknowledged, setSourceRiskAcknowledged] = useState(false);
  const [sourceRiskReason, setSourceRiskReason] = useState("");
  const [savedGrounding, setSavedGrounding] = useState(brief.grounding ?? null);

  useEffect(() => {
    if (!approvedAt) {
      setTimeLeft(null);
      return;
    }
    let id: ReturnType<typeof setInterval>;
    const tick = () => {
      const elapsed = Date.now() - approvedAt;
      const remaining = PASTE_BACK_WINDOW_MS - elapsed;
      if (remaining <= 0) {
        setTimeLeft("Window expired");
        clearInterval(id);
        return;
      }
      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000);
      setTimeLeft(`${mins}:${secs.toString().padStart(2, "0")} to paste URL`);
    };
    tick();
    id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [approvedAt]);

  const draftQ = useBriefDocument(brief.campaign_id, brief.id, "draft", true);
  const approve = useApproveBrief(brief.campaign_id, brandId);
  const nudge = useNudgeBrief(brief.campaign_id, brandId);
  const reject = useRejectBrief(brief.campaign_id, brandId);
  const submitUrl = useSubmitPublishedUrl(brief.campaign_id, brandId);
  const editOutput = useEditOutput(
    draftQ.data?.data?.document_key ? "campaign_document" : undefined,
    draftQ.data?.data?.document_key,
  );

  const isWorking =
    approve.isPending || nudge.isPending || reject.isPending || submitUrl.isPending;

  const draftContent = draftQ.data?.data?.content ?? "";
  const normalizedDraft = draftQ.data?.data?.output_document ?? null;
  const canInlineEdit = Boolean(normalizedDraft)
    && (brief.state === "drafting" || brief.state === "voice_review" || brief.state === "in_review");
  const script = draftContent ? parseScript(draftContent) : null;
  const blockingAlerts = getBlockingReviewAlerts(brief.provenance_alerts);
  const blockingRiskSummary = getBlockingReviewAlertDetails(brief.provenance_alerts);
  const isSourceRiskBlocked = hasBlockingSourceRisk(brief.provenance_alerts);
  const isClaimRiskBlocked = hasClaimRiskBlockedTrust(brief);
  const trustCopy = reviewTrustBadgeCopy(brief);
  const blockingRunId = blockingAlerts.find((alert) => alert.run_id)?.run_id ?? null;
  const canProceedWithAcknowledgedRisk = !isSourceRiskBlocked || (sourceRiskAcknowledged && sourceRiskReason.trim().length > 0);
  const sourceRiskMessage = blockingAlerts[0]?.detail ?? "Resolve source risk before opening YouTube Studio.";
  const claimRiskDetail = claimRiskMessage(brief);
  const recordedPublishedUrl = brief.published_url?.trim() ?? "";
  const publishedHref = safeRecordedUrl(recordedPublishedUrl);
  const recordedPublishedAtValue = brief.published_at;
  const recordedPublishedAt = recordedPublishedAtValue
    ? new Date(recordedPublishedAtValue).toLocaleString()
    : null;
  const needsPublishedUrl = !recordedPublishedUrl
    && (approvedAt !== null || brief.state === "approved" || brief.state === "scheduled");
  const performancePrompt = recordedPublishedUrl
    ? buildPerformanceFollowupPrompt(brief, recordedPublishedUrl)
    : null;
  const performanceFollowupHref = `/agent/${encodeURIComponent(brandId)}?new=1&briefId=${encodeURIComponent(brief.id)}`;
  const primaryActionLabel = recordedPublishedUrl
    ? "Open agent follow-up"
    : needsPublishedUrl
      ? "Mark published"
      : isSourceRiskBlocked
        ? blockingRunId
          ? "Inspect source run"
          : "Fix provenance"
        : isClaimRiskBlocked
          ? "Request safer rewrite"
        : "Open YouTube Studio";

  useEffect(() => {
    setSavedGrounding(brief.grounding ?? null);
  }, [brief.grounding]);

  const metadataText = script
    ? [
        `Title: ${script.title ?? ""}`,
        ``,
        `Description: ${script.description ?? ""}`,
        ``,
        `Tags: ${(script.tags ?? []).join(", ")}`,
      ].join("\n")
    : "";

  function handleDownloadMp4() {
    if (!brief.rendered_asset_url) return;
    const a = document.createElement("a");
    a.href = brief.rendered_asset_url;
    a.download = `${brief.id}.mp4`;
    a.click();
  }

  async function handleOpenYouTubeStudio() {
    setError(null);
    if (isSourceRiskBlocked && !canProceedWithAcknowledgedRisk) {
      setError(sourceRiskMessage);
      return;
    }
    try {
      if (brief.state === "in_review" && !approve.isPending) {
        await approve.mutateAsync(
          isSourceRiskBlocked
            ? {
              briefId: brief.id,
              sourceRiskAcknowledgement: { reason: sourceRiskReason.trim() },
            }
            : brief.id,
        );
        onApproved?.(brief);
      }
      const opened = window.open("https://studio.youtube.com/", "_blank", "noopener,noreferrer");
      if (!opened) {
        setError("Popup blocked — please allow popups and try again, or open YouTube Studio manually.");
      } else {
        setApprovedAt(Date.now());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Approve failed");
    }
  }

  async function handleNudge() {
    setError(null);
    try {
      const result = await nudge.mutateAsync({ briefId: brief.id, message: reason.trim() || undefined });
      navigate(result.data.chat_path);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Nudge failed");
    }
  }

  async function handleReject() {
    setError(null);
    try {
      await reject.mutateAsync({ briefId: brief.id, reason: reason.trim() || "Rejected by operator" });
      setReason("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reject failed");
    }
  }

  async function handleMarkPublished() {
    setError(null);
    try {
      const trimmedPublishedUrl = publishedUrl.trim();
      const result = await submitUrl.mutateAsync({ briefId: brief.id, publishedUrl: trimmedPublishedUrl });
      const nowIso = new Date().toISOString();
      const persistedBrief = result.data.brief ?? {
        ...brief,
        state: "published",
        scheduled_at: null,
        blocked_reason: null,
        published_url: trimmedPublishedUrl,
        published_at: nowIso,
        updated_at: nowIso,
      };
      onPublished?.(persistedBrief);
      setPublishedUrl(persistedBrief.published_url ?? trimmedPublishedUrl);
      setApprovedAt(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Mark published failed");
    }
  }

  async function handleSaveOutput(document: OutputDocument) {
    setError(null);
    const documentKey = draftQ.data?.data?.document_key;
    if (!documentKey) {
      setError("Draft document is not available for editing yet.");
      return;
    }
    try {
      const result = await editOutput.mutateAsync({ output_document: document });
      setSavedGrounding(result.data.grounding);
      await draftQ.refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    }
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-slate-900">{brief.title}</h2>
        <Link className="mt-2 inline-flex text-sm font-semibold text-brand-700" to={`/brand/${encodeURIComponent(brandId)}/publishing/new?content_id=${encodeURIComponent(brief.id)}`}>Prepare in publishing studio →</Link>
        <p className="mt-0.5 text-xs text-slate-500">
          YouTube Personal ·{" "}
          {brief.scheduled_at
            ? new Date(brief.scheduled_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
            : "Unscheduled"}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Badge variant={trustCopy.variant}>{trustCopy.label}</Badge>
          <p className="text-xs text-slate-600">{trustCopy.body}</p>
        </div>
      </div>

      <GroundingIndicator grounding={savedGrounding} />
      {draftQ.data?.data?.document_key && <ReviewAttachments brandId={brandId} campaignId={brief.campaign_id} briefId={brief.id}
        document={draftQ.data.data} editable={['drafting', 'in_review', 'approved'].includes(brief.state)}
        onSaved={async () => { setApprovedAt(null); await draftQ.refetch(); }} />}
      <BriefProvenanceAlerts alerts={brief.provenance_alerts} mode="blocking" />

      {brief.preview_url || brief.rendered_asset_url ? (
        <div className="overflow-hidden rounded-md border border-slate-200">
          <video
            src={brief.preview_url ?? brief.rendered_asset_url ?? undefined}
            controls
            className="w-full max-h-48 bg-black"
          />
          {brief.thumbnail_url && (
            <img src={brief.thumbnail_url} alt="Thumbnail" className="h-16 w-full object-cover object-top border-t border-slate-100" />
          )}
        </div>
      ) : (
        <div className="flex h-28 items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50">
          <p className="text-xs text-slate-400">
            {brief.state === "drafting" ? "Render in progress…" : "No video render yet. Review the draft script below."}
          </p>
        </div>
      )}

      {normalizedDraft ? (
        <OutputView
          document={normalizedDraft}
          titleAlreadyShown={brief.title}
          emptyMessage="No draft script found."
          editable={canInlineEdit}
          isSaving={editOutput.isPending}
          onSave={handleSaveOutput}
        />
      ) : script ? (
        <div className="space-y-2">
          {script.title && (
            <div>
              <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Title</p>
              <p className="text-sm text-slate-800">{script.title}</p>
            </div>
          )}
          {script.description && (
            <div>
              <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Description</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{script.description}</p>
            </div>
          )}
          {script.tags && script.tags.length > 0 && (
            <div>
              <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Tags</p>
              <p className="text-sm text-slate-700">{script.tags.join(", ")}</p>
            </div>
          )}
        </div>
      ) : null}

      {brief.utm_campaign && (
        <div className="rounded-md border border-blue-100 bg-blue-50 p-2">
          <span className="text-xs text-blue-700 font-mono break-all">utm_campaign={brief.utm_campaign}</span>
        </div>
      )}

      <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
        <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Primary action</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {recordedPublishedUrl ? (
            <a
              href={performanceFollowupHref}
              className="inline-flex items-center rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
            >
              {primaryActionLabel}
            </a>
          ) : needsPublishedUrl ? (
            <button
              type="button"
              onClick={handleMarkPublished}
              disabled={isWorking || !publishedUrl.trim()}
              className="rounded-md bg-green-700 px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {primaryActionLabel}
            </button>
          ) : isSourceRiskBlocked ? (
            blockingRunId ? (
              <button
                type="button"
                onClick={() => navigate(buildBrandScopedPath(`/runs/${blockingRunId}`, brandId))}
                className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
              >
                {primaryActionLabel}
              </button>
            ) : (
              <span className="inline-flex items-center rounded-md bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-900">
                {primaryActionLabel}
              </span>
            )
          ) : isClaimRiskBlocked ? (
            <button
              type="button"
              onClick={handleNudge}
              disabled={isWorking}
              title={claimRiskDetail}
              className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-60 hover:bg-amber-700"
            >
              {primaryActionLabel}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleOpenYouTubeStudio}
              disabled={(!canProceedWithAcknowledgedRisk) || isClaimRiskBlocked || isWorking || brief.state === "published"}
              title={isSourceRiskBlocked ? sourceRiskMessage : undefined}
              className="rounded-md bg-[#FF0000] px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-60 hover:bg-red-700"
            >
              {primaryActionLabel}
            </button>
          )}
          <p className="text-xs text-slate-500">
            {recordedPublishedUrl
              ? "Capture follow-up observations now that the published URL is recorded."
              : needsPublishedUrl
                ? "Paste the final YouTube URL and record publication."
                : isSourceRiskBlocked
                  ? "Inspect or repair provenance before treating this upload as publish-ready."
                  : isClaimRiskBlocked
                    ? "Request a safer rewrite before opening YouTube Studio."
                  : "Open YouTube Studio once the draft and metadata are ready."}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {brief.rendered_asset_url && (
          <button
            type="button"
            onClick={handleDownloadMp4}
            disabled={isClaimRiskBlocked}
            title={isClaimRiskBlocked ? claimRiskDetail : undefined}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            Download MP4
          </button>
        )}
        {metadataText && (
          <CopyButton
            text={metadataText}
            label="Copy metadata"
            disabled={(isSourceRiskBlocked) || isClaimRiskBlocked}
            title={isSourceRiskBlocked ? sourceRiskMessage : isClaimRiskBlocked ? claimRiskDetail : undefined}
          />
        )}
        <button
          type="button"
          onClick={handleNudge}
          disabled={isWorking}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Request edits
        </button>
        <button
          type="button"
          onClick={handleReject}
          disabled={isWorking}
          className="rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Reject
        </button>
      </div>

      {isSourceRiskBlocked && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-3">
          <label className="flex items-start gap-2 text-sm text-amber-900">
            <input
              type="checkbox"
              checked={sourceRiskAcknowledged}
              onChange={(event) => setSourceRiskAcknowledged(event.target.checked)}
              className="mt-0.5"
            />
            <span>I understand this draft has blocking source risk and want to proceed to YouTube Studio anyway.</span>
          </label>
          <textarea
            value={sourceRiskReason}
            onChange={(event) => setSourceRiskReason(event.target.value)}
            placeholder="Why are you proceeding despite the missing source evidence?"
            className="min-h-[72px] w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none"
          />
          <p className="text-xs text-amber-800">
            Acknowledgement only unlocks opening YouTube Studio. The dominant action remains inspecting or repairing provenance first.
          </p>
          <ul className="list-disc pl-5 text-xs text-amber-900">
            {blockingRiskSummary.map((detail) => (
              <li key={detail}>{detail}</li>
            ))}
          </ul>
        </div>
      )}

      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Optional note for nudge or reject"
        className="min-h-[56px] w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none"
      />

      {isSourceRiskBlocked && (
        <p className="text-xs font-medium text-amber-800">Resolve source risk before copying metadata or opening YouTube Studio.</p>
      )}
      {isClaimRiskBlocked && (
        <p className="text-xs font-medium text-amber-800">Rewrite unsupported claims before copying metadata or opening YouTube Studio.</p>
      )}

      {recordedPublishedUrl && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-950">
          <p className="font-semibold">Published URL recorded</p>
          {recordedPublishedAt && <p className="mt-1 text-xs text-emerald-800">Recorded {recordedPublishedAt}</p>}
          {publishedHref ? (
            <a href={publishedHref} target="_blank" rel="noreferrer" className="mt-2 block break-all font-medium text-emerald-900 underline">
              {recordedPublishedUrl}
            </a>
          ) : (
            <p className="mt-2 break-all font-medium text-emerald-900">{recordedPublishedUrl}</p>
          )}
          <p className="mt-2 text-xs text-emerald-800">
            Next: verify the final output and start performance follow-up for this upload.
          </p>
          {performancePrompt && (
            <div className="mt-3 flex flex-wrap gap-2">
              <CopyButton text={performancePrompt} label="Copy follow-up prompt" />
              <a
                href={performanceFollowupHref}
                className="inline-flex items-center rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-900 hover:bg-emerald-100"
              >
                Open agent follow-up
              </a>
            </div>
          )}
        </div>
      )}

      {needsPublishedUrl && (
        <div className="space-y-1.5">
          {timeLeft && (
            <p className="text-xs font-medium text-amber-700">{timeLeft}</p>
          )}
          <div className="flex gap-2">
            <input
              value={publishedUrl}
              onChange={(e) => setPublishedUrl(e.target.value)}
              placeholder="Paste YouTube video URL after uploading"
              className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none"
            />
            <button
              type="button"
              onClick={handleMarkPublished}
              disabled={isWorking || !publishedUrl.trim()}
              className="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-xs font-medium text-green-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Save URL
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-xs text-red-700">{error}</p>}
    </div>
  );
}
